<script setup>
// Addendum §2.4 SplitButton — TWO elements. Left button fires the default
// action; the right caret is a SEPARATE button (its own ≥24px target, a
// distinct focusable stop) that opens the menu. aria-haspopup lives on the
// caret only.
import LDropdownMenu from './DropdownMenu.vue'
import LIcon from '../base/Icon.vue'

defineProps({
	label: { type: String, required: true },
	items: { type: Array, default: () => [] },
	variant: { type: String, default: 'primary' }, // primary | secondary
	align: { type: String, default: 'end' },
})
const emit = defineEmits(['default'])
</script>

<template>
	<div class="split" :class="`is-${variant}`">
		<button type="button" class="main" @click="emit('default')">
			<slot name="icon" />
			{{ label }}
		</button>
		<LDropdownMenu :items="items" :align="align">
			<template #trigger="{ toggle, open, onkeydown }">
				<button type="button" class="caret" :class="{ open }" style="height: 100%;" aria-haspopup="menu"
					:aria-expanded="open" :aria-label="`More ${label.toLowerCase()} actions`" @click="toggle"
					@keydown="onkeydown">
					<LIcon name="chevron-down" :size="13" />
				</button>
			</template>
		</LDropdownMenu>
	</div>
</template>

<style scoped>
.split
{
	display: inline-flex;
	align-items: stretch;
	border-radius: var(--r);
}

.main,
.caret
{
	font-family: var(--font-ui);
	font-weight: 600;
	min-height: 36px;
	border: 1px solid transparent;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.main
{
	font-size: var(--text-sm);
	padding: 9px 16px;
	border-radius: var(--r) 0 0 var(--r);
	gap: var(--sp-2);
}

.caret
{
	min-width: var(--target-min);
	padding: 0 8px;
	border-radius: 0 var(--r) var(--r) 0;
}

/* primary */
.is-primary .main,
.is-primary .caret
{
	background: var(--interactive);
	color: var(--interactive-on);
}

.is-primary .caret
{
	border-left: 1px solid var(--on-emphasis-line);
}

.is-primary .main:hover,
.is-primary .caret:hover,
.is-primary .caret.open
{
	background: var(--interactive-hover);
}

/* secondary */
.is-secondary .main,
.is-secondary .caret
{
	background: var(--surface);
	border-color: var(--line);
	color: var(--text-2);
}

.is-secondary .caret
{
	border-left: 1px solid var(--line);
}

.is-secondary .main:hover,
.is-secondary .caret:hover,
.is-secondary .caret.open
{
	background: var(--surface-sunken);
	color: var(--text);
}
</style>
